import {
  objectParser,
  text,
  MAX_MESSAGE_TEXT,
  oneOf,
  booleanAt,
  identifierAt,
  semanticKeyAt,
  nonBlankTextAt,
  valueTextAt,
  confidenceAt,
  fail,
  numberAt,
  nullable,
  nonBlankText,
  arrayOf,
  assertUnique,
  bucketNameAt,
  visibilityAt,
  assertJsonByteSize,
  exactObject,
  enumAt,
  nullableAt,
  arrayAt
} from './primitives';
import {
  type SuperCopyReviewSource,
  SUPER_COPY_BOUNDARY_KINDS,
  type SuperCopyReviewValue,
  SUPER_COPY_REVIEW_STATES,
  type SuperCopyEntity,
  MAX_DOCUMENT_LABEL_LENGTH,
  MAX_DOCUMENT_DEPTH,
  type SuperCopyReviewGroup,
  SUPER_COPY_GROUP_KINDS,
  type SuperCopyReviewClient,
  type SuperCopyReviewDraft,
  type SuperCopyOperationStatus,
  SUPER_COPY_OPERATION_STATES,
  type SuperCopySnapshot,
  SUPER_COPY_PHASES,
  MAX_SUPER_COPY_CLIENTS,
  MAX_SUPER_COPY_GROUPS,
  MAX_SUPER_COPY_VALUES,
  SUPER_COPY_ENTITY_KINDS
} from '../dtos';
import { parseBridgeError } from './context';
import {
  type SuperCopySaveValue,
  type SuperCopySaveGroup,
  type SuperCopySaveClient,
  type SuperCopySavePayload
} from '../protocol';

const MAX_SUPER_COPY_PAYLOAD_BYTES = 1024 * 1024;

export const parseSuperCopyReviewSource = objectParser<SuperCopyReviewSource>({
  pageTitle: text(MAX_MESSAGE_TEXT),
  boundaryKind: oneOf(SUPER_COPY_BOUNDARY_KINDS),
  boundaryLabel: text(MAX_MESSAGE_TEXT),
  truncated: booleanAt
});

export const parseSuperCopyReviewValue = objectParser<SuperCopyReviewValue>({
  id: identifierAt,
  semanticKey: semanticKeyAt,
  label: nonBlankTextAt,
  extractedValue: valueTextAt,
  value: valueTextAt,
  confidence: confidenceAt,
  reviewState: oneOf(SUPER_COPY_REVIEW_STATES),
  accepted: booleanAt
});

export const parseSuperCopyEntity = objectParser<SuperCopyEntity>(
  {
    type: oneOf(SUPER_COPY_ENTITY_KINDS),
    relation: (value, path) => {
      if (typeof value !== 'string' || !/^[a-z][a-z0-9_]{0,79}$/.test(value)) {
        fail(path, 'expected a lowercase record relation');
      }
    },
    confidence: (value, path) => numberAt(value, path, 0.8, 1)
  },
  {
    optional: {
      role: nullable(nonBlankText(MAX_DOCUMENT_LABEL_LENGTH)),
      parentGroupId: nullable(identifierAt)
    }
  }
);

/** Reject malformed ownership rather than silently flattening an orphan/cycle. */
export function assertSuperCopyEntityTree(
  groups: { id: string; entity?: SuperCopyEntity | null }[],
  path = '$.groups'
): void {
  const byId = new Map(groups.map((group) => [group.id, group]));
  for (const group of groups) {
    if (!group.entity) continue;
    const seen = new Set<string>();
    let node: (typeof groups)[number] | undefined = group;
    for (let depth = 2; node; depth++) {
      if (seen.has(node.id) || depth > MAX_DOCUMENT_DEPTH || !node.entity) {
        fail(path, 'record ownership must be an acyclic, classified tree within the depth limit');
      }
      seen.add(node.id);
      const parentId: string | null | undefined = node.entity.parentGroupId;
      if (!parentId) break;
      node = byId.get(parentId);
      if (!node) fail(path, 'record parent must belong to this client');
    }
  }
}

export const parseSuperCopyReviewGroup = objectParser<SuperCopyReviewGroup>(
  {
    id: identifierAt,
    kind: oneOf(SUPER_COPY_GROUP_KINDS),
    label: nonBlankTextAt,
    values: arrayOf(parseSuperCopyReviewValue, MAX_SUPER_COPY_VALUES)
  },
  {
    optional: { entity: nullable(parseSuperCopyEntity) },
    refine: (object, path) => {
      const values = object.values as SuperCopyReviewValue[];
      assertUnique(values, (item) => item.id, `${path}.values`, 'value ID');
    }
  }
);

export const parseSuperCopyReviewClient = objectParser<SuperCopyReviewClient>(
  {
    id: identifierAt,
    suggestedName: bucketNameAt,
    name: bucketNameAt,
    groups: arrayOf(parseSuperCopyReviewGroup, MAX_SUPER_COPY_GROUPS)
  },
  {
    refine: (object, path) => {
      const groups = object.groups as SuperCopyReviewGroup[];
      assertUnique(groups, (item) => item.id, `${path}.groups`, 'group ID');
      assertSuperCopyEntityTree(groups, `${path}.groups`);
    }
  }
);

export const parseSuperCopyReviewDraft = objectParser<SuperCopyReviewDraft>(
  {
    draftId: identifierAt,
    source: parseSuperCopyReviewSource,
    visibility: visibilityAt,
    clients: arrayOf(parseSuperCopyReviewClient, MAX_SUPER_COPY_CLIENTS)
  },
  {
    refine: (object, path) => {
      const clients = object.clients as SuperCopyReviewClient[];
      assertUnique(clients, (item) => item.id, `${path}.clients`, 'client ID');
      assertJsonByteSize(object, path, MAX_SUPER_COPY_PAYLOAD_BYTES);
    }
  }
);

export const parseSuperCopyOperationStatus = objectParser<SuperCopyOperationStatus>({
  clientId: identifierAt,
  operationId: identifierAt,
  state: oneOf(SUPER_COPY_OPERATION_STATES),
  bucketId: nullable(identifierAt),
  bucketName: nullable(nonBlankTextAt),
  error: nullable(parseBridgeError)
});

export function parseSuperCopySnapshot(value: unknown, path = '$'): SuperCopySnapshot {
  const object = exactObject(value, path, [
    'phase',
    'completedDraftId',
    'draft',
    'operations',
    'error'
  ]);
  const phase = enumAt(object.phase, SUPER_COPY_PHASES, `${path}.phase`);
  nullableAt(object.completedDraftId, `${path}.completedDraftId`, identifierAt);
  nullableAt(object.draft, `${path}.draft`, parseSuperCopyReviewDraft);
  const operations = arrayAt(
    object.operations,
    `${path}.operations`,
    parseSuperCopyOperationStatus
  );
  assertUnique(operations, (item) => item.clientId, `${path}.operations`, 'client ID');
  assertUnique(operations, (item) => item.operationId, `${path}.operations`, 'operation ID');
  nullableAt(object.error, `${path}.error`, parseBridgeError);
  // Every producer already holds these invariants; enforcing them here means
  // the sidebar never has to render a review phase without a review or a
  // "saved" copy whose buckets are not all there.
  if (phase === 'saved') {
    if (object.completedDraftId === null) {
      fail(`${path}.completedDraftId`, 'must identify the completed draft');
    }
    if (operations.length === 0 || operations.some((item) => item.state !== 'COMPLETE')) {
      fail(`${path}.operations`, 'must all be complete once the draft is saved');
    }
  } else if (object.completedDraftId !== null) {
    fail(`${path}.completedDraftId`, 'must be null until the draft is saved');
  }
  if ((phase === 'reviewing' || phase === 'saving') && object.draft === null) {
    fail(`${path}.draft`, `is required while ${phase}`);
  }
  return value as SuperCopySnapshot;
}

const superCopySaveValueAt = objectParser<SuperCopySaveValue>({
  id: identifierAt,
  semanticKey: semanticKeyAt,
  label: nonBlankTextAt,
  value: valueTextAt,
  reviewState: oneOf(SUPER_COPY_REVIEW_STATES),
  accepted: booleanAt
});

const superCopySaveGroupAt = objectParser<SuperCopySaveGroup>(
  {
    id: identifierAt,
    kind: oneOf(SUPER_COPY_GROUP_KINDS),
    label: nonBlankTextAt,
    values: arrayOf(superCopySaveValueAt, MAX_SUPER_COPY_VALUES)
  },
  {
    optional: { entity: nullable(parseSuperCopyEntity) },
    refine: (object, path) => {
      const values = object.values as SuperCopySaveValue[];
      if (values.length === 0 && !object.entity) {
        fail(`${path}.values`, 'must contain values or source-backed record structure');
      }
      assertUnique(values, (item) => item.id, `${path}.values`, 'value ID');
    }
  }
);

const superCopySaveClientAt = objectParser<SuperCopySaveClient>(
  {
    id: identifierAt,
    name: bucketNameAt,
    groups: arrayOf(superCopySaveGroupAt, MAX_SUPER_COPY_GROUPS)
  },
  {
    refine: (object, path) => {
      const groups = object.groups as SuperCopySaveGroup[];
      if (groups.length === 0) fail(`${path}.groups`, 'must contain at least one group');
      assertUnique(groups, (item) => item.id, `${path}.groups`, 'group ID');
      assertSuperCopyEntityTree(groups, `${path}.groups`);
    }
  }
);

export const parseSuperCopySavePayload = objectParser<SuperCopySavePayload>(
  {
    draftId: identifierAt,
    visibility: visibilityAt,
    clients: arrayOf(superCopySaveClientAt, MAX_SUPER_COPY_CLIENTS)
  },
  {
    refine: (object, path) => {
      const clients = object.clients as SuperCopySaveClient[];
      if (clients.length === 0) fail(`${path}.clients`, 'must contain at least one client');
      assertUnique(clients, (item) => item.id, `${path}.clients`, 'client ID');
      assertJsonByteSize(object, path, MAX_SUPER_COPY_PAYLOAD_BYTES);
    }
  }
);
